import {
  Button,
  Dialog,
  ReportIcon as Report,
  Typography,
  WarningIcon as Warning,
} from "@iluvatar/ui-kit";

import { useCallback, useState } from "react";

import { useStore } from "../contexts/dataStore.context";

// types
type ConfirmationActionType = {
  label: string;
  type?: "error" | "warning" | "success" | "info";
  focused?: boolean;
  onClick?: () => void;
};

export type ConfirmationModalType = {
  open: boolean;
  type?: "warning" | "danger";
  title?: string | React.ReactElement;
  content?: string | React.ReactElement;
  actions: ConfirmationActionType[];
};

export const ConfirmationModal: React.FC = () => {
  const [modal] = useStore("confirmationModal");
  const [open, setOpen] = useStore("confirmationModal.open");
  const [error, setModalError] = useState<unknown>();

  const [disabled, setDisabled] = useState(false);

  /**
   * Functions
   */
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  if (!open && error) {
    setModalError(undefined);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          handleClose();
        }
      }}
      dismissible={!disabled}
      title={modal?.title}
    >
      {modal.type === "warning" && (
        <div className="flex f-c-c items-center justify-center mt-16 mb-16">
          <Warning className="[font-size:large] [color:var(--color-chart--warning--dark)]" />
          <Typography
            variant="h3"
            className="[margin-left:8px] [margin-right:8px] [margin-top:8px]"
          >
            Warning
          </Typography>
        </div>
      )}
      {modal.type === "danger" && (
        <div className="flex f-c-c items-center justify-center mt-16 mb-16">
          <Report className="[font-size:large] [color:var(--color-chart--overdue--dark)]" />
          <Typography
            variant="h3"
            className="[margin-left:8px] [margin-right:8px] [margin-top:8px]"
          >
            Danger
          </Typography>
        </div>
      )}
      <div className="">
        <>
          <div
            id="confirmation-dialog-description"
            className="mb-4"
            style={{ textAlign: "center" }}
          >
            {modal?.content}
          </div>
          {error && <div className="mb-4">{`${error}`}</div>}
        </>
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-2">
        {modal.actions.map((action, key) => (
          <Button
            disabled={disabled}
            key={`${action.label}.${key}`}
            variant={action.type === "error" ? "danger" : "ghost"}
            onClick={async () => {
              setDisabled(true);
              try {
                if (action?.onClick) {
                  await action.onClick();
                }
                handleClose();
                // oxlint-disable-next-line
              } catch (err: any) {
                if (typeof err === "string") {
                  setModalError(err);
                } else if (typeof err === "object") {
                  setModalError(err.message);
                } else {
                  setModalError(err.toString());
                }
              } finally {
                setDisabled(false);
              }
            }}
            autoFocus={action?.focused}
          >
            {action?.label}
          </Button>
        ))}
      </div>
    </Dialog>
  );
};
